import React, { useEffect, useState } from 'react';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { Group, ReportTemplate, useGetAllGroups } from '@ais-ui/websockets';
import { PiQuestion } from 'react-icons/pi';
import { cn } from '@ais-ui/utils';
import { useLocalization } from '@ais-ui/locale-management';
import { reportTypeToIcon } from './reportsTypes';
import { useReportContext } from './ReportContext';
import { ReportTemplateDelete } from './Mutations/DeleteReportTemplate';
import { ScheduleReports } from './Mutations/ScheduleReportTemplate';
import { ReportScheduleDelete } from './Mutations/DeleteReportSchedule';
import { getGroupsFromIds } from './utils';
import { ShowLastReportButton } from './ShowLastReportButton';
import { ScheduleDetails } from './ScheduleDetails';
import { ReportItem } from './ReportItem';
import { ReportTemplateDropDownMenu } from './ReportTemplateDropDownMenu';

interface ReportTemplateItemProps {
  reportTemplate: ReportTemplate;
}

export const ReportTemplateItem = React.memo(
  ({ reportTemplate }: ReportTemplateItemProps) => {
    const { messages } = useLocalization();
    const [reportTemplateGroups, setReportTemplateGroups] = useState<Group[]>(
      []
    );
    const { data: groups } = useGetAllGroups();
    const { setSelectedJob, setSelectedReportTemplate } = useReportContext();

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showScheduleDialog, setShowScheduleDialog] = useState(false);
    const [showDeleteScheduleDialog, setShowDeleteScheduleDialog] =
      useState(false);

    const onDelete = () => setShowDeleteDialog(true);
    const onSchedule = () => setShowScheduleDialog(true);
    const onDeleteSchedule = () => setShowDeleteScheduleDialog(true);

    useEffect(() => {
      if (groups) {
        setReportTemplateGroups(getGroupsFromIds(reportTemplate, groups));
      }
    }, [groups, reportTemplate]);

    const availableJobs = reportTemplate.jobs.filter((report) => report.uuid);

    const ReportIconComponent = Object.prototype.hasOwnProperty.call(
      reportTypeToIcon,
      reportTemplate.type
    )
      ? reportTypeToIcon[reportTemplate.type]
      : PiQuestion;

    const isScheduled =
      reportTemplate.isScheduled &&
      reportTemplate.schedule?.interval !== 'disabled';

    let statusMessage;
    switch (reportTemplate.tplneweststatus) {
      case 'FINISHED':
        statusMessage = messages.reportsTemplateItemLastReportSuccess;
        break;
      case 'FAILED':
        statusMessage = messages.reportsTemplateItemLastReportFailed;
        break;
      case 'NONE':
        statusMessage = messages.reportsTemplateItemNoReports;
        break;
      case 'EXECUTING':
        statusMessage = messages.reportsTemplateItemReportIsExecuting;
        break;
      case 'PENDING':
        statusMessage = messages.reportsTemplateItemReportIsPending;
        break;
      case 'FINISHED_SCHEDULED':
        statusMessage = messages.reportsTemplateItemReportFinishedSchedule;
        break;
      case 'ABORTED':
        statusMessage = messages.reportsTemplateItemLastReportAborted;
        break;
      default:
        statusMessage = '';
        break;
    }

    return (
      <AccordionItem
        id={`report-template-item-${reportTemplate.tplName}`}
        key={reportTemplate.tplUuid}
        value={reportTemplate.tplUuid}
        className={cn('pr-4 pl-2')}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <div className="flex space-x-1 items-center">
              <ShowLastReportButton
                report={
                  availableJobs && availableJobs.length > 0
                    ? availableJobs[availableJobs.length - 1]
                    : undefined
                }
                reportTemplate={reportTemplate}
                setSelectedReport={setSelectedJob}
                setSelectedReportTemplate={setSelectedReportTemplate}
              />
              <Separator orientation="vertical" className="h-6 bg-gray-300" />
            </div>
            <div className="flex ml-4 items-center">
              <Tooltip>
                <TooltipTrigger>
                  <ReportIconComponent
                    className={cn(
                      'h-4 w-4 group-hover/report-type-item:text-primary',
                      reportTemplate.tplneweststatus === 'FINISHED' &&
                        'text-success',
                      reportTemplate.tplneweststatus === 'FAILED' &&
                        'text-destructive',
                      reportTemplate.tplneweststatus === 'EXECUTING' &&
                        'text-blue-600',
                      reportTemplate.tplneweststatus === 'PENDING' &&
                        'text-slate-600',
                      reportTemplate.tplneweststatus === 'ABORTED' &&
                        'text-orange-400'
                    )}
                  />
                </TooltipTrigger>
                <TooltipContent>{statusMessage}</TooltipContent>
              </Tooltip>
              <AccordionTrigger
                className="w-[205px]"
                hideChevron={true}
                onClick={() => setSelectedReportTemplate(reportTemplate)}
              >
                <div className="ml-3 space-y-1">
                  <p className="text-sm text-left font-medium leading-none group-hover:underline">
                    {reportTemplate.tplName}
                  </p>
                </div>
              </AccordionTrigger>
            </div>
          </div>
          <ReportTemplateDropDownMenu
            reportTemplate={reportTemplate}
            onDelete={onDelete}
            onSchedule={onSchedule}
            onDeleteSchedule={onDeleteSchedule}
            isScheduled={isScheduled}
          />
        </div>

        <AccordionContent className="w-full">
          <h2 className="pl-2 pb-2 text-md">
            {messages.reportsTemplateItemSharedLabel}
          </h2>
          <p className="pl-6 pb-4 text-muted-foreground">
            {reportTemplateGroups.length > 0
              ? reportTemplateGroups.map((group) => group.name).join(', ')
              : messages.reportsTemplateItemSharedEmptyValue}
          </p>
          <h2 className="pl-2 pb-2 pt-4 text-md">
            {messages.reportsTemplateItemScheduleLabel}
          </h2>
          {!isScheduled && (
            <p className="pl-6 pb-4 text-muted-foreground">
              {messages.reportsTemplateItemScheduleEmptyValue}
            </p>
          )}
          {isScheduled && <ScheduleDetails reportTemplate={reportTemplate} />}
          <h2 className="pl-2 pb-2 pt-4 text-md">
            {messages.reportsTemplateItemReportsLabel}
          </h2>
          <div className="space-y-2">
            {availableJobs.length === 0 && (
              <div className="px-6">
                {messages.reportsTemplateItemReportsEmptyValue}
              </div>
            )}
            {availableJobs.map((report) => (
              <ReportItem
                key={report.uuid}
                report={report}
                setSelectedJob={setSelectedJob}
              />
            ))}
          </div>
        </AccordionContent>

        {showDeleteDialog && (
          <ReportTemplateDelete
            reportTemplate={reportTemplate}
            showDialog={showDeleteDialog}
            setShowDialog={setShowDeleteDialog}
          />
        )}
        {!isScheduled && showScheduleDialog && (
          <ScheduleReports
            reportTemplate={reportTemplate}
            showDialog={showScheduleDialog}
            setShowDialog={setShowScheduleDialog}
          />
        )}
        {isScheduled && (
          <ReportScheduleDelete
            reportTemplate={reportTemplate}
            showDialog={showDeleteScheduleDialog}
            setShowDialog={setShowDeleteScheduleDialog}
          />
        )}
      </AccordionItem>
    );
  }
);
